// ============================================================================
// CONFIGURATION - Single Source of Truth
// ============================================================================

/**
 * Single source of truth for all feature flags.
 * Adding a new flag requires editing only this configuration.
 *
 * To add a new flag:
 * 1. Add an entry to this array with key, externalKey, and fallback
 * 2. Optionally add to InternalFeatureFlag and ExternalFeatureFlag enums for backward compatibility
 * 3. That's it! Types and mappings are generated automatically.
 *
 * Kept dependency-free so it can be imported from e2e/ alongside src/.
 */
export const FEATURE_FLAG_CONFIG = [
  {
    key: 'isProductAssociatedItemNumbersCsvImportEnabled',
    externalKey: 'csv-import-product-item-association',
    fallback: false,
  },
  {
    key: 'hasPricingPolicySubscription',
    externalKey: 'policy-pricing',
    fallback: false,
  },
  {
    key: 'isProductsManagementEnabled',
    externalKey: 'product-view-management',
    fallback: false,
  },
  {
    key: 'isProductsCsvImportEnabled',
    externalKey: 'csv-import-product',
    fallback: false,
  },
  {
    key: 'isProductPricingImportEnabled',
    externalKey: 'csv-import-product-pricing-controls',
    fallback: false,
  },
  {
    key: 'isProductPricingCsvExportEnabled',
    externalKey: 'csv-export-product-pricing-controls-for-import',
    fallback: false,
  },
  {
    key: 'isProductsCsvExportEnabled',
    externalKey: 'csv-export-product-for-import',
    fallback: false,
  },
  {
    key: 'isDistributorManagementEnabled',
    externalKey: 'sub-distributor-management',
    fallback: false,
  },
  {
    key: 'isTestPurchaseGoogleDocLinkEnabled',
    externalKey: 'exp-list-tp-gdoc-link',
    fallback: false,
  },
  {
    key: 'isEcosystemExplorerEnabled',
    externalKey: 'exp-overview-map',
    fallback: false,
  },
  {
    key: 'isManualTestPurchaseEnabled',
    externalKey: 'manual-test-purchases',
    fallback: false,
  },
  {
    key: 'isAnalyticsTrackingEnabled',
    externalKey: 'analytics-tracking',
    fallback: false,
  },
  {
    key: 'isSentryEnabled',
    externalKey: 'fe-error-tracking',
    fallback: false,
  },
  {
    key: 'isMultipleBotsDeleteEnabled',
    externalKey: 'exp-multiple-bots-delete',
    fallback: false,
  },
  {
    key: 'isEnforcementCommentMentionsEnabled',
    externalKey: 'rol-enforcement-comment-mentions',
    fallback: true,
  },
  {
    key: 'isCompanySupportTeamEnabled',
    externalKey: 'rol-company-support-team',
    fallback: false,
  },
  {
    key: 'isMultipleBotsFromBroadKeywordEnabled',
    externalKey: 'exp-multiple-bots-from-broad-keyword',
    fallback: false,
  },
  {
    key: 'isCustomerNotificationsTemplatesManagementEnabled',
    externalKey: 'customer-notification-management',
    fallback: false,
  },
  {
    key: 'isSellerVerificationFeeEnabled',
    externalKey: 'exp-seller-verification-fee',
    fallback: false,
  },
  {
    key: 'isInAppNotificationsEnabled',
    externalKey: 'exp-in-app-notifications-enabled',
    fallback: false,
  },
  {
    key: 'isTrustedSourcesEnabled',
    externalKey: 'trusted-sources',
    fallback: false,
  },
  {
    key: 'isListingProductMatchesEnabled',
    externalKey: 'exp-listing-product-matches-enabled',
    fallback: false,
  },
  {
    key: 'isTrustedSourceCsvImportEnabled',
    externalKey: 'csv-import-trusted-source',
    fallback: false,
  },
  {
    key: 'isProductImageCsvImportEnabled',
    externalKey: 'csv-import-product-image',
    fallback: false,
  },
  {
    key: 'isProductContentCsvImportEnabled',
    externalKey: 'csv-import-product-content',
    fallback: false,
  },
  {
    key: 'isStoreEmailForCustomerEnabled',
    externalKey: 'exp-per-customer-seller-emails',
    fallback: false,
  },
  {
    key: 'idleLogout',
    externalKey: 'inactivity-sign-out',
    fallback: { enabled: false, timeoutMinutes: null },
  },
  {
    key: 'isEnforcementConversationsEnabled',
    externalKey: 'rol-enforcement-conversations',
    fallback: false,
  },
  {
    key: 'isImageGalleryEnabled',
    externalKey: 'image-gallery',
    fallback: false,
  },
  {
    key: 'isMarketPricingDashboardEnabled',
    externalKey: 'market-pricing-dashboard',
    fallback: false,
  },
] as const;

export type FeatureFlagKey = typeof FEATURE_FLAG_CONFIG[number]['key'];
export type FeatureFlagExternalKey = typeof FEATURE_FLAG_CONFIG[number]['externalKey'];

/**
 * Type representing all available feature flags with their data types.
 * This type is automatically derived from FEATURE_FLAG_CONFIG.
 */
export type FeatureFlags = {
  [Flag in typeof FEATURE_FLAG_CONFIG[number]as Flag['key']]: Flag['fallback'];
};
